import React from 'react'

import { ZariContext } from '_zari/context'
import { Modal, Button, Table, Col, Row, Form } from '_zari/core'

import { IdGen } from 'utils'

import * as RequerimientoService from 'services/requerimiento.service'

const RequerimientosEnAlmacenModal = ({ show, handleClose, onSeleccionar }) => {
  const __zariContext = React.useContext(ZariContext)

  const [_keyTabla, setKeyTabla] = React.useState(IdGen.newId())
  const [requerimientos, setRequerimientos] = React.useState([])
  const [idsMarcados, setIdsMarcados] = React.useState([])
  const [filtroCorrelativo, setFiltroCorrelativo] = React.useState('')
  const [filtroOficina, setFiltroOficina] = React.useState('')

  // Determinar la oficina del primer requerimiento seleccionado para bloquear
  const oficinaBloqueada = React.useMemo(() => {
    if (idsMarcados.length === 0) return null
    const primerMarcado = requerimientos.find(r => r._id === idsMarcados[0])
    return primerMarcado ? primerMarcado._idOficinaSolicitante : null
  }, [idsMarcados, requerimientos])

  const onChangeCheckbox = React.useCallback(id => {
    setIdsMarcados(prev => {
      if (prev.some(x => x === id)) {
        return prev.filter(x => x !== id)
      }
      return [...prev, id]
    })
  }, [])

  const requerimientosFiltrados = React.useMemo(() => {
    return (requerimientos || []).filter(row => {
      if (filtroCorrelativo) {
        const corr = `${row.correlativo ?? ''}`.toUpperCase()
        if (!corr.includes(filtroCorrelativo.toUpperCase())) return false
      }
      if (filtroOficina) {
        const ofi = `${row.oficinaSolicitante ?? ''}`.toUpperCase()
        if (!ofi.includes(filtroOficina.toUpperCase())) return false
      }
      return true
    })
  }, [requerimientos, filtroCorrelativo, filtroOficina])

  const clickSeleccionar = React.useCallback(() => {
    if (!idsMarcados || idsMarcados.length === 0) {
      __zariContext.snack('warning', 'Seleccionar al menos un requerimiento')
      return
    }

    __zariContext.loader.open('Obteniendo productos de requerimientos.')

    RequerimientoService.obtenerDetallesParaSalida(idsMarcados)
      .then(({ data }) => {
        const detallesArr = Array.isArray(data) ? data : []
        if (detallesArr.length === 0) {
          __zariContext.snack(
            'warning',
            'Los requerimientos seleccionados no tienen ítems pendientes para despachar desde almacén.'
          )
          return
        }
        const primerReq = requerimientos.find(r => r._id === idsMarcados[0])
        const infoRequerimiento = primerReq ? {
          _idOficinaSolicitante: primerReq._idOficinaSolicitante || '',
          _idUsuarioSolicitante: primerReq._idUsuario || ''
        } : null
        onSeleccionar(idsMarcados, detallesArr, infoRequerimiento)
        handleClose()
      })
      .catch(err => {
        const error = err?.data?.error || 'Error al obtener detalles'
        __zariContext.snack('error', error)
      })
      .finally(() => {
        __zariContext.loader.close()
      })
  }, [__zariContext, idsMarcados, handleClose, onSeleccionar])

  React.useEffect(() => {
    if (!show) return

    __zariContext.loader.open('Obteniendo requerimientos en almacen.')

    RequerimientoService.obtenerEnAlmacenParaSalida()
      .then(({ data }) => {
        setKeyTabla(IdGen.newId())
        setRequerimientos(data)
        setIdsMarcados([])
      })
      .catch(err => {
        const error = err?.data?.error || 'Error al obtener requerimientos'
        __zariContext.snack('error', error)
      })
      .finally(() => {
        __zariContext.loader.close()
      })
  }, [show, __zariContext])

  return (
    <Modal show={show} size='lg'>
      <Modal.Header>
        <Modal.Title>Requerimientos en Almacen</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='mb-2'>
          <Col md={4}>
            <input
              type='text'
              className='form-control form-control-sm'
              placeholder='Filtrar por correlativo...'
              value={filtroCorrelativo}
              onChange={e => setFiltroCorrelativo(e.target.value)}
            />
          </Col>
          <Col md={4}>
            <input
              type='text'
              className='form-control form-control-sm'
              placeholder='Filtrar por oficina...'
              value={filtroOficina}
              onChange={e => setFiltroOficina(e.target.value)}
            />
          </Col>
        </Row>
        {oficinaBloqueada && (
          <Row className='mb-2'>
            <Col md={12}>
              <small className='text-info'>
                Solo se pueden seleccionar requerimientos de la misma oficina.
              </small>
            </Col>
          </Row>
        )}
        <Row>
          <Col md={12}>
            <Table
              key={_keyTabla}
              containerId='table-requerimientos-en-almacen'
              filas={requerimientosFiltrados}
              headRows={[
                { label: '' },
                { label: 'CORRELATIVO' },
                { label: 'FECHA' },
                { label: 'GLOSA' },
                { label: 'USUARIO' },
                { label: 'OFICINA' }
              ]}
              maxRowsShown={10}
              rowTemplate={(row, k) => {
                const deshabilitado = oficinaBloqueada && row._idOficinaSolicitante !== oficinaBloqueada && !idsMarcados.some(x => x === row._id)
                return (
                  <Table.Row key={k} style={deshabilitado ? { opacity: 0.5 } : {}}>
                    <Table.Cell>
                      <Form.Checkbox
                        checked={idsMarcados.some(x => x === row._id)}
                        onChange={() => onChangeCheckbox(row._id)}
                        id={`${row._id}-checkbox-req-almacen`}
                        label=''
                        disabled={deshabilitado}
                      />
                    </Table.Cell>
                    <Table.Cell>{row.correlativo ?? '--'}</Table.Cell>
                    <Table.Cell>{row.fechaEmision ?? '--'}</Table.Cell>
                    <Table.Cell>{row.glosa ?? '--'}</Table.Cell>
                    <Table.Cell>{row.nombreUsuario ?? '--'}</Table.Cell>
                    <Table.Cell>{row.oficinaSolicitante ?? '--'}</Table.Cell>
                  </Table.Row>
                )
              }}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button outline tiny color='secondary' onClick={handleClose}>
          Cancelar
        </Button>
        <Button tiny onClick={clickSeleccionar}>
          Seleccionar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default React.memo(RequerimientosEnAlmacenModal)
